// Presentational parts for the Governance intelligence drawer.
import {
  AlertTriangle,
  CheckCircle2,
  Landmark,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Stamp,
  User,
} from "lucide-react";
import { SectionCard, ScoreRing, StatTile } from "../Domain";
import {
  DOC_TYPE_COLORS,
  DOC_TYPE_FALLBACK_BADGE_CLS,
  LAYER_ICON_BOX_TONE,
} from "../constants";
import { formatDocType, toTitleCaseFromSnake } from "../utils";
import type {
  GApproval,
  GOwnership,
  GReviewCycle,
  GovernanceDisplayData,
} from "./governanceTypes";
import {
  AI_BADGE_CLS,
  AI_LABEL_CLS,
  AI_WRAP_CLS,
  APPROVAL_ICON_BOX_CLS,
  APPROVAL_ICON_CLS,
  DOCUMENTED_PILL_CLS,
  GOVERNANCE_DRAWER_COPY,
  GOVERNANCE_MISSING_BANNER,
  OVERALL_SCORE_TEXT_CLS,
  POSTURE_PILL_CLS,
  REVIEW_ICON_BOX_CLS,
  REVIEW_ICON_CLS,
  VALIDATED_DOT_CLS,
  VALIDATED_PILL_CLS,
  cycleStatusTone,
  roleIconClass,
} from "./utilities";

function DocTypeBadge({ documentType }: { documentType: string }) {
  const key = documentType.toUpperCase();
  const toneCls = DOC_TYPE_COLORS[key] ?? DOC_TYPE_FALLBACK_BADGE_CLS;
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${toneCls}`}
    >
      {formatDocType(key)}
    </span>
  );
}

export interface GovernanceDrawerHeaderProps {
  documentTitle?: string;
  documentType?: string;
  documentMeta: GovernanceDisplayData["document"] | undefined;
  backendNote?: string;
  overallPct: string;
}

export function GovernanceDrawerHeader({
  documentTitle,
  documentType,
  documentMeta,
  backendNote,
  overallPct,
}: GovernanceDrawerHeaderProps) {
  const titleText = documentTitle || documentMeta?.title;
  const typeText = documentType || documentMeta?.document_type;

  return (
    <header className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-card p-4 pr-14 sm:p-6 sm:pr-16 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${LAYER_ICON_BOX_TONE.governance.box}`}>
          <Landmark className={`size-5 ${LAYER_ICON_BOX_TONE.governance.icon}`} />
        </div>
        <div className="min-w-0 flex flex-col gap-1.5">
          <div className="text-lg font-bold text-foreground">
            {GOVERNANCE_DRAWER_COPY.title}
          </div>
          <p className="text-xs text-muted-foreground max-w-xl break-words">
            {GOVERNANCE_DRAWER_COPY.headerBlurb}
          </p>
          {backendNote && (
            <p className="text-[11px] text-muted-foreground max-w-xl break-words">
              {backendNote}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {titleText && (
              <span className="inline-flex items-center rounded-md border border-border bg-background/60 px-2 py-0.5 text-[11px] text-foreground break-words">
                {titleText}
              </span>
            )}
            {typeText && <DocTypeBadge documentType={typeText} />}
            <span className={VALIDATED_PILL_CLS}>
              <span className={VALIDATED_DOT_CLS} />
              {GOVERNANCE_DRAWER_COPY.validatedLabel}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-end">
          <span className="text-[11px] text-muted-foreground">
            {GOVERNANCE_DRAWER_COPY.overallLabel}
          </span>
          <span className={OVERALL_SCORE_TEXT_CLS}>{overallPct}</span>
        </div>
      </div>
    </header>
  );
}


export function GovernanceMissingBanner({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div className={GOVERNANCE_MISSING_BANNER.wrapCls}>
      <AlertTriangle className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">
        <div className={GOVERNANCE_MISSING_BANNER.titleCls}>
          {count} governance control{count === 1 ? "" : "s"} missing
        </div>
        <div className="mt-0.5 opacity-90">
          {GOVERNANCE_MISSING_BANNER.guidance}
        </div>
      </div>
    </div>
  );
}

export function OwnershipCard({ item }: { item: GOwnership }) {
  const responsibilities = item.responsibilities ?? [];
  const clarity = item.clarity_score ?? 0;
  return (
    <div className="rounded-lg border border-border bg-card/60 p-3">
      <div className="flex items-start gap-3 min-w-0">
        <div className="flex size-9 flex-none items-center justify-center rounded-md bg-background border border-border">
          <User className={`size-4 ${roleIconClass(item.role_type)}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-[13px] font-semibold text-foreground break-words">
              {item.role}
            </span>
            <span className="inline-flex items-center rounded-md border border-border bg-background/60 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {item.role_type}
            </span>
          </div>
          {item.named_party && (
            <div className="mt-0.5 text-[11.5px] text-foreground break-words">
              {item.named_party}
            </div>
          )}
          {responsibilities.length > 0 && (
            <ul className="mt-1.5 list-disc pl-4 text-[11.5px] text-muted-foreground">
              {responsibilities.map((r, i) => (
                <li key={i} className="break-words">
                  {r}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex items-center justify-between text-[10.5px] text-muted-foreground">
            <span className="break-words">{item.source_location}</span>
            <span className="tabular-nums">
              Clarity · {Math.round(clarity * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ApprovalRow({ item }: { item: GApproval }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-card/60 p-3">
      <div className={APPROVAL_ICON_BOX_CLS}>
        <Stamp className={APPROVAL_ICON_CLS} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-foreground break-words">
            {item.authority}
          </span>
          <span className="text-[10.5px] text-muted-foreground">
            {toTitleCaseFromSnake(item.approval_type)}
          </span>
          {item.documented && (
            <span className={DOCUMENTED_PILL_CLS}>
              <CheckCircle2 className="size-3" />
              Documented
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {item.effective_date && <span>Effective · {item.effective_date}</span>}
          <span>· {item.source_location}</span>
        </div>
      </div>
    </div>
  );
}

export function ReviewCycleRow({ item }: { item: GReviewCycle }) {
  const tone = cycleStatusTone(item.status);
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-card/60 p-3">
      <div className={REVIEW_ICON_BOX_CLS}>
        <RefreshCcw className={REVIEW_ICON_CLS} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-foreground break-words">
            {item.activity}
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${tone.bg} ${tone.text}`}
          >
            {tone.label}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground break-words">
          <span>Frequency · {item.frequency}</span>
          <span>· Owner · {item.owner}</span>
          <span>· {item.source_location}</span>
        </div>
      </div>
    </div>
  );
}

export interface GovernanceHighlightProps {
  overallPct: string;
  strongControls: number;
  weakControls: number;
  missing: number;
  namedOwners: number;
  approvers: number;
  cycles: number;
}

export function PrimaryGovernanceHighlight({
  overallPct,
  strongControls,
  weakControls,
  missing,
  namedOwners,
  approvers,
  cycles,
}: GovernanceHighlightProps) {
  return (
    <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_260px]">
      <div className="min-w-0 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className={POSTURE_PILL_CLS}>
            {GOVERNANCE_DRAWER_COPY.postureLabel}
          </span>
          <span className="text-lg font-bold text-foreground break-words">
            {strongControls} strong · {weakControls} weak · {missing} missing
          </span>
        </div>

        <div className={AI_WRAP_CLS}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className={AI_BADGE_CLS}>
              <Sparkles className="size-3" />
              {GOVERNANCE_DRAWER_COPY.aiLabel}
            </span>
            <span className={AI_LABEL_CLS}>
              {GOVERNANCE_DRAWER_COPY.assessmentLabel}
            </span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-foreground/90 break-words">
            {namedOwners} accountable role{namedOwners === 1 ? "" : "s"} and{" "}
            {approvers} approval authorit{approvers === 1 ? "y" : "ies"} are
            named. {cycles} review cycle{cycles === 1 ? "" : "s"} are defined
            with cadence and owner. Address the {missing} missing control
            {missing === 1 ? "" : "s"} to lift the posture toward "Strong".
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <StatTile value={strongControls} label="Strong controls" tone="emerald" />
          <StatTile value={weakControls} label="Weak controls" tone="amber" />
          <StatTile value={namedOwners} label="Named owners" tone="cyan" />
          <StatTile value={cycles} label="Review cycles" tone="indigo" />
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 min-w-0">
        <div className="w-full text-center text-xs text-muted-foreground">
          {GOVERNANCE_DRAWER_COPY.governanceIndexLabel}
        </div>
        <ScoreRing
          value={overallPct}
          icon={<ShieldCheck className="size-5 text-emerald-400" />}
          caption={GOVERNANCE_DRAWER_COPY.postureCaption}
        />
      </div>
    </div>
  );
}

/* Re-export SectionCard so the drawer only needs a single import path. */
export { SectionCard };
